import { useState, useMemo, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useAuthStore } from '../../lib/auth';
import Card from '../../components/Card';
import Input from '../../components/Input';
import Select from '../../components/Select';
import Textarea from '../../components/Textarea';
import Button from '../../components/Button';
import Modal from '../../components/Modal';
import AddWebsiteModal from '../../components/common/AddWebsiteModal';
import EditWebsiteModal from '../../components/common/EditWebsiteModal';
import apiClient, { updateWebsite, setPrimaryWebsite } from '../../lib/api';

// Industries list (same as onboarding)
const INDUSTRIES = [
  { value: '', label: 'Select your industry' },
  { value: 'technology', label: 'Technology & Software' },
  { value: 'ecommerce', label: 'E-commerce & Retail' },
  { value: 'marketing', label: 'Marketing & Advertising' },
  { value: 'healthcare', label: 'Healthcare & Medical' },
  { value: 'finance', label: 'Finance & Banking' },
  { value: 'education', label: 'Education & Training' },
  { value: 'realestate', label: 'Real Estate' },
  { value: 'hospitality', label: 'Hospitality & Travel' },
  { value: 'manufacturing', label: 'Manufacturing & Industrial' },
  { value: 'consulting', label: 'Consulting & Services' },
  { value: 'other', label: 'Other' },
];

// Countries list (same as onboarding)
const COUNTRIES = [
  { value: '', label: 'Select country' },
  { value: 'NL', label: '🇳🇱 Netherlands' },
  { value: 'BE', label: '🇧🇪 Belgium' },
  { value: 'DE', label: '🇩🇪 Germany' },
  { value: 'FR', label: '🇫🇷 France' },
  { value: 'GB', label: '🇬🇧 United Kingdom' },
  { value: 'US', label: '🇺🇸 United States' },
  { value: 'ES', label: '🇪🇸 Spain' },
  { value: 'IT', label: '🇮🇹 Italy' },
];

// Company info validation schema (website is managed separately in Websites section)
const companyInfoSchema = z.object({
  industry: z.string().optional(),
  description: z.string().optional(),
  country: z.string().optional(),
  phone: z.string().optional(),
});

type CompanyInfoForm = z.infer<typeof companyInfoSchema>;

export default function WorkspaceSettingsPage() {
  const { t } = useTranslation();
  const { workspace, setWorkspace } = useAuthStore();

  console.log('WorkspaceSettingsPage - workspace:', workspace);
  console.log('WorkspaceSettingsPage - websites:', workspace?.websites);

  // Split slug into editable prefix and fixed suffix
  const { slugPrefix, slugSuffix } = useMemo(() => {
    if (!workspace?.slug) return { slugPrefix: '', slugSuffix: '' };

    // Find the last dash to split the slug
    const lastDashIndex = workspace.slug.lastIndexOf('-');
    if (lastDashIndex === -1) {
      // No dash found, entire slug is editable
      return { slugPrefix: workspace.slug, slugSuffix: '' };
    }

    return {
      slugPrefix: workspace.slug.substring(0, lastDashIndex),
      slugSuffix: workspace.slug.substring(lastDashIndex), // includes the dash
    };
  }, [workspace?.slug]);

  const [workspaceName, setWorkspaceName] = useState(workspace?.name || '');
  const [editableSlugPart, setEditableSlugPart] = useState(slugPrefix);
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [error, setError] = useState('');
  const [isRedirecting, setIsRedirecting] = useState(() => {
    // Check if we're in the middle of a redirect from previous page load
    return sessionStorage.getItem('workspace-redirecting') === 'true';
  });
  const [showAddWebsiteModal, setShowAddWebsiteModal] = useState(false);
  const [showEditWebsiteModal, setShowEditWebsiteModal] = useState(false);
  const [websiteToEdit, setWebsiteToEdit] = useState<{ id: string; name: string; domain: string; description?: string } | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [websiteToDelete, setWebsiteToDelete] = useState<{ id: string; name: string } | null>(null);
  const [deleteError, setDeleteError] = useState('');

  // Company info form
  const {
    register,
    handleSubmit,
    formState: { errors, isDirty: isCompanyInfoDirty },
    reset: resetCompanyInfo,
    setValue,
  } = useForm<CompanyInfoForm>({
    resolver: zodResolver(companyInfoSchema),
    defaultValues: {
      industry: workspace?.settings?.onboarding?.industry || '',
      description: workspace?.settings?.onboarding?.description || '',
      country: workspace?.settings?.onboarding?.country || '',
      phone: workspace?.settings?.onboarding?.phone || '',
    },
  });

  // Fetch full workspace data on mount (including settings)
  useEffect(() => {
    const fetchWorkspaceData = async () => {
      if (!workspace?.id) return;

      console.log('Fetching full workspace data for ID:', workspace.id);
      try {
        const response = await apiClient.get(`/workspace/${workspace.id}`);
        const fullWorkspace = response.data.data?.workspace || response.data.workspace;
        console.log('Fetched full workspace data:', fullWorkspace);
        if (fullWorkspace) {
          setWorkspace(fullWorkspace);
        }
      } catch (err) {
        console.error('Failed to fetch workspace data:', err);
      }
    };

    fetchWorkspaceData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run only once on mount

  // Clear redirect flag when component successfully loads
  useEffect(() => {
    // If we successfully loaded the page, clear the redirect flag
    if (workspace && isRedirecting) {
      sessionStorage.removeItem('workspace-redirecting');
      setIsRedirecting(false);
    }
  }, [workspace, isRedirecting]);

  // Update editable slug part when workspace changes
  useEffect(() => {
    if (workspace?.name) {
      setWorkspaceName(workspace.name);
    }
    if (slugPrefix) {
      setEditableSlugPart(slugPrefix);
    }
    // Reset company info form when workspace changes
    const onboardingData = workspace?.settings?.onboarding;
    console.log('WorkspaceSettingsPage - Resetting form with data:', onboardingData);
    resetCompanyInfo({
      industry: onboardingData?.industry || '',
      description: onboardingData?.description || '',
      country: onboardingData?.country || '',
      phone: onboardingData?.phone || '',
    });
  }, [workspace, slugPrefix, resetCompanyInfo]);

  const handleCreateWebsite = async (websiteData: {
    domain: string;
    name: string;
    description?: string;
  }) => {
    try {
      // Check for duplicate domain (frontend validation)
      const normalizedDomain = websiteData.domain.toLowerCase().trim()
        .replace(/^https?:\/\//, '') // Remove protocol if present
        .replace(/^www\./, '');      // Remove www. for comparison

      const existingWebsite = workspace?.websites?.find(w => {
        const existingDomain = w.domain.toLowerCase().replace(/^www\./, '');
        return existingDomain === normalizedDomain;
      });

      if (existingWebsite) {
        throw new Error(t('addWebsite.errors.duplicate', `Website ${websiteData.domain} already exists in this workspace`));
      }

      console.log('[WORKSPACE SETTINGS] Creating website:', websiteData);
      const response = await apiClient.post('/websites', websiteData);

      console.log('[WORKSPACE SETTINGS] Response:', response.data);

      // Backend returns { success: true, data: { website, brandExtractionJobId } }
      const newWebsite = response.data.data?.website || response.data.website;

      if (newWebsite) {
        console.log('[WORKSPACE SETTINGS] Website created successfully:', newWebsite);

        // Fetch fresh workspace data from API to ensure we have the latest state
        const meResponse = await apiClient.get('/auth/me');
        console.log('[WORKSPACE SETTINGS] Fetched workspace:', meResponse.data);

        if (meResponse.data.workspace) {
          console.log('[WORKSPACE SETTINGS] Setting fresh workspace with websites:', meResponse.data.workspace.websites);
          setWorkspace(meResponse.data.workspace);
        }
      }
    } catch (error: any) {
      console.error('Failed to create website:', error);
      throw new Error(error.response?.data?.message || t('addWebsite.errors.failed', 'Failed to add website'));
    }
  };

  const handleDeleteWebsite = async () => {
    if (!websiteToDelete) return;

    try {
      setDeleteError(''); // Clear previous errors
      await apiClient.delete(`/websites/${websiteToDelete.id}`);

      // Update workspace state reactively
      if (workspace) {
        const updatedWorkspace = {
          ...workspace,
          websites: (workspace.websites || []).filter(w => w.id !== websiteToDelete.id),
          // Clear primary/active if the deleted website was primary/active
          primaryWebsiteId: workspace.primaryWebsiteId === websiteToDelete.id
            ? null
            : workspace.primaryWebsiteId,
          activeWebsiteId: workspace.activeWebsiteId === websiteToDelete.id
            ? null
            : workspace.activeWebsiteId,
        };
        setWorkspace(updatedWorkspace);
      }

      setIsDeleteModalOpen(false);
      setWebsiteToDelete(null);
      setDeleteError('');
    } catch (err: any) {
      console.error('Failed to delete website:', err);
      // Show the actual error message from the API in the modal
      const errorMessage = err.response?.data?.message ||
        err.response?.data?.error ||
        t('settings.workspace.websites.deleteModal.error', 'Failed to delete website. Please try again.');
      setDeleteError(errorMessage);
      // Keep modal open so user can see the error
    }
  };

  const handleEditWebsite = async (websiteData: { id: string; name: string; description?: string }) => {
    try {
      console.log('[WEBSITE] Updating website:', websiteData);
      await updateWebsite(websiteData.id, {
        name: websiteData.name,
        description: websiteData.description,
      });

      // Refresh workspace data
      const workspaceResponse = await apiClient.get('/auth/me');
      if (workspaceResponse.data.workspace) {
        console.log('[WEBSITE] Workspace refreshed after update');
        setWorkspace(workspaceResponse.data.workspace);
      }
    } catch (error: any) {
      console.error('Failed to update website:', error);
      throw new Error(error.response?.data?.message || t('editWebsite.errors.failed', 'Failed to update website'));
    }
  };

  const handleSetPrimary = async (websiteId: string) => {
    try {
      console.log('[WEBSITE] Setting website as primary:', websiteId);
      await setPrimaryWebsite(websiteId);

      // Refresh workspace data
      const workspaceResponse = await apiClient.get('/auth/me');
      if (workspaceResponse.data.workspace) {
        console.log('[WEBSITE] Workspace refreshed after setting primary');
        setWorkspace(workspaceResponse.data.workspace);
      }
    } catch (error: any) {
      console.error('Failed to set primary website:', error);
      setError(error.response?.data?.message || 'Failed to set primary website');
    }
  };

  // Construct full slug from editable part + fixed suffix
  const fullSlug = editableSlugPart + slugSuffix;

  const handleSave = async () => {
    if (!workspace) return;

    const slugChanged = fullSlug !== workspace.slug;

    // If slug changed, show overlay immediately BEFORE API call
    if (slugChanged) {
      setIsRedirecting(true);
      // Persist redirect state across page reload
      sessionStorage.setItem('workspace-redirecting', 'true');
    }

    setIsSaving(true);
    setError('');
    setSaveSuccess(false);

    try {
      // Call API to update workspace
      const response = await apiClient.put(`/auth/workspace`, {
        name: workspaceName,
        slug: fullSlug,
      });

      // Update local state with response data
      const updatedWorkspace = response.data.data?.workspace || response.data.workspace;
      if (updatedWorkspace) {
        // Preserve websites array from current workspace (backend doesn't return it in PUT response)
        setWorkspace({
          ...updatedWorkspace,
          websites: workspace.websites, // Keep existing websites
          primaryWebsiteId: workspace.primaryWebsiteId, // Keep primary website ID
          activeWebsiteId: workspace.activeWebsiteId, // Keep active website ID
        });
      }

      // If slug changed, redirect to new URL
      if (slugChanged) {
        // Immediate redirect - overlay will persist via sessionStorage
        window.location.href = `/${fullSlug}/settings/workspace`;
      } else {
        // Only show success message if slug didn't change
        setSaveSuccess(true);
        setTimeout(() => setSaveSuccess(false), 3000);
      }
    } catch (err: any) {
      console.error('Failed to update workspace:', err);
      // If slug change failed, hide overlay and show error
      if (slugChanged) {
        setIsRedirecting(false);
        sessionStorage.removeItem('workspace-redirecting');
      }
      setError(err.response?.data?.message || 'Failed to update workspace');
    } finally {
      // Don't set isSaving to false if we're redirecting
      if (!slugChanged) {
        setIsSaving(false);
      }
    }
  };

  const handleSaveCompanyInfo = async (data: CompanyInfoForm) => {
    if (!workspace) return;

    setIsSaving(true);
    setError('');
    setSaveSuccess(false);

    try {
      // Get clean onboarding data (avoid nested settings bug)
      const currentOnboarding = workspace.settings?.onboarding || {};

      // Only keep the actual onboarding fields, not nested settings
      const cleanOnboarding = {
        companyName: currentOnboarding.companyName,
        website: currentOnboarding.website, // Keep existing website (managed separately)
        industry: currentOnboarding.industry,
        description: currentOnboarding.description,
        country: currentOnboarding.country,
        phone: currentOnboarding.phone,
        contentSources: currentOnboarding.contentSources,
        selectedPlatforms: currentOnboarding.selectedPlatforms,
        onboardingCompleted: currentOnboarding.onboardingCompleted,
      };

      // Merge with new data (website is NOT updated here, managed in Websites section)
      const updatedSettings = {
        onboarding: {
          ...cleanOnboarding,
          industry: data.industry || null,
          description: data.description || null,
          country: data.country || null,
          phone: data.phone || null,
        },
      };

      const response = await apiClient.patch(`/workspace/${workspace.id}`, {
        settings: updatedSettings,
      });

      const updatedWorkspace = response.data.data?.workspace || response.data.workspace;
      if (updatedWorkspace) {
        // Preserve websites array from current workspace (backend doesn't return it in PATCH response)
        setWorkspace({
          ...updatedWorkspace,
          websites: workspace.websites, // Keep existing websites
          primaryWebsiteId: workspace.primaryWebsiteId, // Keep primary website ID
          activeWebsiteId: workspace.activeWebsiteId, // Keep active website ID
        });
      }

      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err: any) {
      console.error('Failed to update company info:', err);
      setError(err.response?.data?.message || 'Failed to update company information');
    } finally {
      setIsSaving(false);
    }
  };

  const hasChanges =
    workspaceName !== workspace?.name || fullSlug !== workspace?.slug;

  return (
    <>
      {/* Redirecting Overlay - Rendered as Portal to cover everything */}
      {isRedirecting && createPortal(
        <div className="fixed inset-0 bg-white dark:bg-gray-900 flex items-center justify-center" style={{ zIndex: 9999 }}>
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-gray-200 dark:border-gray-700 border-t-black dark:border-t-white mb-4"></div>
            <h2 className="text-2xl font-bold dark:text-white mb-2">
              {t('settings.workspace.redirecting.title', 'Redirecting to new workspace URL...')}
            </h2>
            <p className="text-gray-600 dark:text-gray-400">
              {t('settings.workspace.redirecting.description', 'Please wait while we update your workspace URL')}
            </p>
          </div>
        </div>,
        document.body
      )}

      <div className="space-y-6">
        {/* Page Header */}
        <div>
          <h1 className="text-3xl font-bold dark:text-white">
            {t('settings.workspace.title', 'Workspace Settings')}
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-2">
            {t('settings.workspace.description', 'Manage your workspace name, URL, and members')}
          </p>
        </div>

      {/* General Information */}
      <Card>
        <h2 className="text-xl font-bold mb-4 dark:text-white">
          {t('settings.workspace.general.title', 'General Information')}
        </h2>

        <div className="space-y-4">
          {/* Workspace Name */}
          <Input
            label={t('settings.workspace.general.name', 'Workspace Name')}
            value={workspaceName}
            onChange={(e) => setWorkspaceName(e.target.value)}
            placeholder="My Workspace"
          />

          {/* Workspace Slug */}
          <div>
            <label className="block text-sm font-bold mb-2 dark:text-gray-200">
              {t('settings.workspace.general.slug', 'Workspace Slug')}
            </label>
            <div className="flex items-center gap-2">
              <Input
                value={editableSlugPart}
                onChange={(e) => setEditableSlugPart(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-'))}
                placeholder="my-workspace"
                className="flex-1"
              />
              {slugSuffix && (
                <span className="text-gray-500 dark:text-gray-400 font-mono text-sm px-3 py-2 bg-gray-100 dark:bg-gray-800 border-2 border-gray-300 dark:border-gray-600 rounded-lg">
                  {slugSuffix}
                </span>
              )}
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              {t('settings.workspace.general.slugHint', 'You can only edit the first part. The random suffix cannot be changed.')}
            </p>
            <div className="mt-2 p-3 bg-yellow-50 dark:bg-yellow-900/20 border-2 border-yellow-400 dark:border-yellow-600 rounded-lg">
              <p className="text-sm text-yellow-800 dark:text-yellow-200">
                <strong>{t('settings.workspace.general.slugWarning', 'Warning:')}</strong>{' '}
                {t(
                  'settings.workspace.general.slugWarningMessage',
                  'Changing the slug will change your workspace URL. All members will need to use the new URL.'
                )}
              </p>
              <p className="text-sm text-yellow-700 dark:text-yellow-300 mt-1 font-mono">
                {t('settings.workspace.general.currentUrl', 'Current URL:')} {window.location.origin}/{workspace?.slug}
              </p>
              {fullSlug !== workspace?.slug && (
                <p className="text-sm text-yellow-700 dark:text-yellow-300 mt-1 font-mono">
                  {t('settings.workspace.general.newUrl', 'New URL:')} {window.location.origin}/{fullSlug}
                </p>
              )}
            </div>
          </div>

          {/* Created Date */}
          <div>
            <label className="block text-sm font-bold mb-2 dark:text-gray-200">
              {t('settings.workspace.general.created', 'Created')}
            </label>
            <p className="text-gray-600 dark:text-gray-400">
              {/* TODO: Get actual creation date from API */}
              {new Date().toLocaleDateString()}
            </p>
          </div>

          {/* Save Button */}
          {error && (
            <div className="p-4 bg-red-50 dark:bg-red-900/20 border-2 border-red-500 rounded-lg">
              <p className="text-sm text-red-900 dark:text-red-200">{error}</p>
            </div>
          )}

          {saveSuccess && (
            <div className="p-4 bg-green-50 dark:bg-green-900/20 border-2 border-green-500 rounded-lg">
              <p className="text-sm text-green-900 dark:text-green-200">
                {t('settings.workspace.general.saveSuccess', 'Workspace updated successfully!')}
              </p>
            </div>
          )}

          <Button
            variant="primary"
            onClick={handleSave}
            isLoading={isSaving}
            disabled={!hasChanges || isSaving}
          >
            {t('settings.workspace.general.save', 'Save Changes')}
          </Button>
        </div>
      </Card>

      {/* Websites Management */}
      <Card>
        <div className="flex items-start justify-between mb-4">
          <div>
            <h2 className="text-xl font-bold dark:text-white">
              {t('settings.workspace.websites.title', 'Websites')}
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              {t('settings.workspace.websites.description', 'Manage websites for brand extraction and taxonomy generation')}
            </p>
          </div>
          {workspace?.websites && (
            <span className="text-xs font-semibold px-3 py-1.5 rounded-full whitespace-nowrap bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200">
              {workspace.websites.length} / 2 {t('settings.workspace.websites.websitesLabel', 'websites')} (free)
            </span>
          )}
        </div>

        {/* Add Website Button */}
        <div className="mb-6">
          <Button
            onClick={() => setShowAddWebsiteModal(true)}
            variant="primary"
            disabled={workspace?.websites && workspace.websites.length >= 2}
            title={
              workspace?.websites && workspace.websites.length >= 2
                ? t('settings.workspace.websites.limitReached', 'Website limit reached (2/2). Upgrade your plan to add more websites.')
                : undefined
            }
          >
            {t('settings.workspace.websites.addButton', 'Add Website')}
          </Button>
          {workspace?.websites && workspace.websites.length >= 2 && (
            <p className="text-sm text-amber-600 dark:text-amber-400 mt-2">
              {t('settings.workspace.websites.limitWarning', 'You have reached the maximum of 2 websites on the free plan.')}
            </p>
          )}
        </div>

        {/* Your Websites List */}
        {workspace?.websites && workspace.websites.length > 0 ? (
          <div>
            <h3 className="text-sm font-bold mb-3 dark:text-white uppercase tracking-wide">
              {t('settings.workspace.websites.yourWebsites', 'Your Websites')}
            </h3>
            <div className="space-y-2">
              {workspace.websites.map((website) => (
                <div
                  key={website.id}
                  className="flex items-center justify-between gap-3 p-3 bg-white dark:bg-gray-700 rounded-lg border-2 border-black dark:border-white"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <p className="text-sm font-semibold dark:text-gray-100">
                        {website.name}
                      </p>
                      {website.isPrimary && (
                        <span className="text-xs font-semibold px-2 py-0.5 rounded bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200">
                          PRIMARY
                        </span>
                      )}
                    </div>
                    <p className="text-xs font-mono text-gray-600 dark:text-gray-400 truncate">
                      {website.domain}
                    </p>
                    {website.description && (
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        {website.description}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        setWebsiteToEdit(website);
                        setShowEditWebsiteModal(true);
                      }}
                      className="flex-shrink-0 px-3 py-1.5 text-sm font-semibold text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600 rounded border-2 border-gray-700 dark:border-gray-300 transition-colors"
                    >
                      {t('settings.workspace.websites.edit', 'Edit')}
                    </button>
                    {!website.isPrimary && (
                      <button
                        type="button"
                        onClick={() => handleSetPrimary(website.id)}
                        className="flex-shrink-0 px-3 py-1.5 text-sm font-semibold text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded border-2 border-blue-600 dark:border-blue-400 transition-colors"
                      >
                        {t('settings.workspace.websites.setPrimary', 'Set Primary')}
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => {
                        setWebsiteToDelete({ id: website.id, name: website.name });
                        setIsDeleteModalOpen(true);
                      }}
                      className="flex-shrink-0 px-3 py-1.5 text-sm font-semibold text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 rounded border-2 border-red-600 dark:border-red-400 transition-colors"
                    >
                      {t('settings.workspace.websites.remove', 'Remove')}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="text-center py-8 border-2 border-dashed border-gray-300 dark:border-gray-700 rounded-lg">
            <p className="text-gray-500 dark:text-gray-400">
              {t('settings.workspace.websites.empty', 'No websites added yet. Add your first website above.')}
            </p>
          </div>
        )}
      </Card>

      {/* Company Information */}
      <Card>
        <h2 className="text-xl font-bold mb-4 dark:text-white">
          {t('settings.workspace.company.title', 'Company Information')}
        </h2>

        <form onSubmit={handleSubmit(handleSaveCompanyInfo)} className="space-y-4">
          <Select
            label={t('settings.workspace.company.industry', 'Industry')}
            error={errors.industry?.message}
            {...register('industry')}
          >
            {INDUSTRIES.map((industry) => (
              <option key={industry.value} value={industry.value}>
                {industry.label}
              </option>
            ))}
          </Select>

          <Textarea
            label={t('settings.workspace.company.description', 'Description')}
            placeholder={t('settings.workspace.company.descriptionPlaceholder', 'Brief description of your company...')}
            rows={4}
            error={errors.description?.message}
            {...register('description')}
          />

          <Select
            label={t('settings.workspace.company.country', 'Country')}
            error={errors.country?.message}
            {...register('country')}
          >
            {COUNTRIES.map((country) => (
              <option key={country.value} value={country.value}>
                {country.label}
              </option>
            ))}
          </Select>

          <Input
            label={t('settings.workspace.company.phone', 'Phone')}
            type="tel"
            placeholder="+31 6 12345678"
            error={errors.phone?.message}
            {...register('phone')}
          />

          {error && (
            <div className="p-4 bg-red-50 dark:bg-red-900/20 border-2 border-red-500 rounded-lg">
              <p className="text-sm text-red-900 dark:text-red-200">{error}</p>
            </div>
          )}

          {saveSuccess && (
            <div className="p-4 bg-green-50 dark:bg-green-900/20 border-2 border-green-500 rounded-lg">
              <p className="text-sm text-green-900 dark:text-green-200">
                {t('settings.workspace.company.saveSuccess', 'Company information updated successfully!')}
              </p>
            </div>
          )}

          <Button
            type="submit"
            variant="primary"
            isLoading={isSaving}
            disabled={!isCompanyInfoDirty || isSaving}
          >
            {t('settings.workspace.company.save', 'Save Changes')}
          </Button>
        </form>
      </Card>

      {/* Members & Permissions */}
      <Card>
        <h2 className="text-xl font-bold mb-4 dark:text-white">
          {t('settings.workspace.members.title', 'Members & Permissions')}
        </h2>

        <div className="text-center py-8">
          <div className="text-4xl mb-2">👥</div>
          <p className="text-gray-500 dark:text-gray-400 font-medium">
            {t('settings.workspace.members.comingSoon', 'Team member management coming soon')}
          </p>
          <p className="text-sm text-gray-400 dark:text-gray-500 mt-2">
            {t(
              'settings.workspace.members.comingSoonDesc',
              'Invite team members, manage roles, and control permissions'
            )}
          </p>
        </div>
      </Card>

      {/* Danger Zone */}
      <Card className="border-red-500 dark:border-red-600">
        <h2 className="text-xl font-bold mb-4 text-red-600 dark:text-red-400">
          {t('settings.workspace.danger.title', 'Danger Zone')}
        </h2>

        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
          {t(
            'settings.workspace.danger.description',
            'Deleting your workspace is permanent and cannot be undone. All posts, content, and settings will be lost.'
          )}
        </p>

        <Button variant="secondary" disabled className="opacity-50 cursor-not-allowed">
          {t('settings.workspace.danger.delete', 'Delete Workspace (Coming Soon)')}
        </Button>
      </Card>
      </div>

      {/* Add Website Modal */}
      <AddWebsiteModal
        open={showAddWebsiteModal}
        onClose={() => setShowAddWebsiteModal(false)}
        onCreate={handleCreateWebsite}
      />

      {/* Edit Website Modal */}
      <EditWebsiteModal
        open={showEditWebsiteModal}
        onClose={() => {
          setShowEditWebsiteModal(false);
          setWebsiteToEdit(null);
        }}
        onUpdate={handleEditWebsite}
        website={websiteToEdit}
      />

      {/* Delete Website Confirmation Modal */}
      <Modal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setWebsiteToDelete(null);
          setDeleteError(''); // Clear error on close
        }}
        title={t('settings.workspace.websites.deleteModal.title', 'Remove Website')}
        size="sm"
      >
        <div className="space-y-4">
          <p className="text-gray-700 dark:text-gray-300">
            {t('settings.workspace.websites.deleteModal.confirm', 'Are you sure you want to remove this website?')}
          </p>

          {websiteToDelete && (
            <div className="p-3 bg-gray-100 dark:bg-gray-800 rounded border border-gray-300 dark:border-gray-700">
              <p className="text-sm font-semibold text-gray-900 dark:text-white">
                {websiteToDelete.name}
              </p>
            </div>
          )}

          <p className="text-sm text-gray-600 dark:text-gray-400">
            {t('settings.workspace.websites.deleteModal.warning', 'This action cannot be undone. All data associated with this website will be lost.')}
          </p>

          {deleteError && (
            <div className="text-sm text-red-600 dark:text-red-400 p-3 border-2 border-red-600 dark:border-red-400 rounded-lg bg-red-50 dark:bg-red-900/20">
              {deleteError}
            </div>
          )}

          <div className="flex gap-3 justify-end">
            <Button
              variant="secondary"
              onClick={() => {
                setIsDeleteModalOpen(false);
                setWebsiteToDelete(null);
                setDeleteError(''); // Clear error on cancel
              }}
            >
              {t('common.cancel', 'Cancel')}
            </Button>
            <Button
              variant="primary"
              onClick={handleDeleteWebsite}
              className="bg-red-600 hover:bg-red-700 border-red-600"
            >
              {t('settings.workspace.websites.deleteModal.remove', 'Remove')}
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
}
